<template>
  <section class="page">
    <div class="page__head">
      <h2 class="page__title">{{ $t('adminDict.title') }}</h2>
      <div class="page__tabs">
        <button
          v-for="t in tabs"
          :key="t.value"
          class="page__tab"
          :class="{ 'is-active': tab === t.value }"
          @click="tab = t.value"
        >{{ t.label }}</button>
      </div>
    </div>

    <div v-if="error" class="error-banner">{{ error }}</div>

    <!-- ЗВАНИЯ -->
    <div v-if="tab === 'ranks'" class="section">
      <div class="section__head">
        <h3 class="section__title">{{ $t('adminDict.sections.ranks') }}</h3>
        <ActionButton :color="'#2A8A1B'" :text="$t('adminDict.buttons.addRank')" @click="openCreate('rank')" />
      </div>
      <table class="tbl">
        <thead>
          <tr>
            <th style="width:80px">{{ $t('adminDict.columns.id') }}</th>
            <th>{{ $t('adminDict.columns.name') }}</th>
            <th style="width:200px">{{ $t('adminDict.columns.group') }}</th>
            <th style="width:120px"></th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="r in ranks" :key="r.id">
            <td>{{ r.id }}</td>
            <td>{{ localizedName(r) }}</td>
            <td>{{ r.group }}</td>
            <td>
              <div class="actions">
                <button class="actions__btn actions__btn--edit" @click="openEdit('rank', r)" :title="$t('common.edit')" :aria-label="$t('common.edit')">
                  <img class="actions__icon" src="@/assets/images/edit.svg" alt="edit" />
                </button>
                <button class="actions__btn actions__btn--delete" @click="onDelete('rank', r)" :title="$t('common.delete')" :aria-label="$t('common.delete')">
                  <img class="actions__icon" src="@/assets/images/markdown/trash.svg" alt="delete" />
                </button>
              </div>
            </td>
          </tr>
          <tr v-if="!ranks.length"><td colspan="4" class="empty">{{ $t('adminDict.empty') }}</td></tr>
        </tbody>
      </table>
    </div>

    <!-- ДОЛЖНОСТИ -->
    <div v-if="tab === 'positions'" class="section">
      <div class="section__head">
        <h3 class="section__title">{{ $t('adminDict.sections.positions') }}</h3>
        <ActionButton :color="'#2A8A1B'" :text="$t('adminDict.buttons.addPosition')" @click="openCreate('position')" />
      </div>
      <table class="tbl">
        <thead>
          <tr>
            <th style="width:80px">{{ $t('adminDict.columns.id') }}</th>
            <th>{{ $t('adminDict.columns.name') }}</th>
            <th style="width:200px">{{ $t('adminDict.columns.roleCode') }}</th>
            <th style="width:120px"></th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="p in positions" :key="p.id">
            <td>{{ p.id }}</td>
            <td>{{ localizedName(p) }}</td>
            <td><code v-if="p.code">{{ p.code }}</code><span v-else class="muted">—</span></td>
            <td>
              <div class="actions">
                <button class="actions__btn actions__btn--edit" @click="openEdit('position', p)" :title="$t('common.edit')" :aria-label="$t('common.edit')">
                  <img class="actions__icon" src="@/assets/images/edit.svg" alt="edit" />
                </button>
                <button class="actions__btn actions__btn--delete" @click="onDelete('position', p)" :title="$t('common.delete')" :aria-label="$t('common.delete')">
                  <img class="actions__icon" src="@/assets/images/markdown/trash.svg" alt="delete" />
                </button>
              </div>
            </td>
          </tr>
          <tr v-if="!positions.length"><td colspan="4" class="empty">{{ $t('adminDict.empty') }}</td></tr>
        </tbody>
      </table>
    </div>

    <!-- ВИДЫ П/Н -->
    <div v-if="tab === 'duties'" class="section">
      <div class="section__head">
        <h3 class="section__title">{{ $t('adminDict.sections.duties') }}</h3>
        <ActionButton :color="'#2A8A1B'" :text="$t('adminDict.buttons.addDuty')" @click="openCreate('duty')" />
      </div>
      <table class="tbl">
        <thead>
          <tr>
            <th style="width:100px">{{ $t('adminDict.columns.code') }}</th>
            <th>{{ $t('adminDict.columns.name') }}</th>
            <th style="width:130px">{{ $t('adminDict.columns.requiresStpn') }}</th>
            <th style="width:100px">{{ $t('adminDict.columns.sortOrder') }}</th>
            <th style="width:100px">{{ $t('adminDict.columns.active') }}</th>
            <th style="width:120px"></th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="d in duties" :key="d.id">
            <td><code>{{ d.code }}</code></td>
            <td>{{ localizedName(d) }}</td>
            <td>{{ d.requires_stpn ? $t('common.yes') : $t('common.no') }}</td>
            <td>{{ d.sort_order }}</td>
            <td>{{ d.is_active ? $t('common.yes') : $t('common.no') }}</td>
            <td>
              <div class="actions">
                <button class="actions__btn actions__btn--edit" @click="openEdit('duty', d)" :title="$t('common.edit')" :aria-label="$t('common.edit')">
                  <img class="actions__icon" src="@/assets/images/edit.svg" alt="edit" />
                </button>
                <button class="actions__btn actions__btn--delete" @click="onDelete('duty', d)" :title="$t('common.delete')" :aria-label="$t('common.delete')">
                  <img class="actions__icon" src="@/assets/images/markdown/trash.svg" alt="delete" />
                </button>
              </div>
            </td>
          </tr>
          <tr v-if="!duties.length"><td colspan="6" class="empty">{{ $t('adminDict.empty') }}</td></tr>
        </tbody>
      </table>
    </div>

    <!-- Модалка -->
    <div v-if="modal.open" class="modal" @click.self="closeModal">
      <div class="modal__panel">
        <h3 class="modal__title">
          {{ modal.editingId ? $t('adminDict.modal.editing') : $t('adminDict.modal.adding') }} —
          {{ $t(`adminDict.modal.kinds.${modal.kind}`) }}
        </h3>
        <div class="form">
          <div v-if="modal.kind === 'duty'" class="form__row">
            <label class="form__label">{{ $t('adminDict.modal.abbr') }} <span class="req">*</span></label>
            <input class="form__input" v-model="modal.form.code" :placeholder="$t('adminDict.modal.abbrPh')" />
          </div>
          <div class="form__row">
            <label class="form__label">{{ $t('common.name') }} <span class="req">*</span></label>
            <input class="form__input" v-model="modal.form.name" :placeholder="$t('adminDict.modal.namePh')" />
          </div>
          <div v-if="modal.kind === 'rank'" class="form__row">
            <label class="form__label">{{ $t('adminDict.modal.group') }}</label>
            <input class="form__input" v-model="modal.form.group" :placeholder="$t('adminDict.modal.groupPh')" />
          </div>
          <div v-if="modal.kind === 'position'" class="form__row">
            <label class="form__label">{{ $t('adminDict.modal.roleCode') }}</label>
            <input class="form__input" v-model="modal.form.code" :placeholder="$t('adminDict.modal.roleCodePh')" />
            <div class="hint">{{ $t('adminDict.modal.roleCodeHint') }}</div>
          </div>
          <template v-if="modal.kind === 'duty'">
            <div class="form__row">
              <label class="form__label">{{ $t('adminDict.modal.sortOrder') }}</label>
              <input class="form__input" type="number" v-model.number="modal.form.sort_order" />
            </div>
            <div class="form__row form__row--check">
              <label class="checkbox"><input type="checkbox" v-model="modal.form.requires_stpn" /> {{ $t('adminDict.modal.requiresStpn') }}</label>
              <label v-if="modal.editingId" class="checkbox"><input type="checkbox" v-model="modal.form.is_active" /> {{ $t('adminDict.modal.isActive') }}</label>
            </div>
          </template>
          <div v-if="modal.error" class="form__error">{{ modal.error }}</div>
        </div>
        <div class="modal__footer">
          <button class="btn btn--secondary" @click="closeModal">{{ $t('common.cancel') }}</button>
          <button class="btn btn--primary" :disabled="modal.saving" @click="onSubmit">
            {{ modal.saving ? $t('common.saving') : (modal.editingId ? $t('common.save') : $t('common.create')) }}
          </button>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import {
  getRanks, createRank, updateRank, deleteRank,
  getPositions, createPosition, updatePosition, deletePosition,
} from '@/api/personnel'
import { listDutyTypes, createDutyType, updateDutyType, deleteDutyType } from '@/api/dutyTypes'
import ActionButton from '@/components/widgets/ActionButton.vue'
import { localizedName } from '@/utils/localizedName'

const emptyRank = () => ({ name: '', group: '' })
const emptyPos  = () => ({ name: '', code: '' })
const emptyDuty = () => ({ code: '', name: '', requires_stpn: false, sort_order: 100 })

export default {
  name: 'AdminDictionariesView',
  components: { ActionButton },
  data() {
    return {
      tab: 'ranks',
      ranks: [],
      positions: [],
      duties: [],
      error: null,
      modal: {
        open: false,
        kind: null, // 'rank' | 'position'
        editingId: null,
        form: emptyRank(),
        saving: false,
        error: null,
      },
    }
  },
  computed: {
    tabs() {
      return [
        { value: 'ranks',     label: this.$t('adminDict.tabs.ranks') },
        { value: 'positions', label: this.$t('adminDict.tabs.positions') },
        { value: 'duties',    label: this.$t('adminDict.tabs.duties') },
      ]
    },
  },
  mounted() {
    this.fetchAll()
  },
  methods: {
    localizedName(entity) {
      return localizedName(entity, this.$i18n.locale)
    },
    async fetchAll() {
      try {
        this.error = null
        const [r, p, d] = await Promise.all([
          getRanks(),
          getPositions(),
          listDutyTypes({ include_inactive: 1 }),
        ])
        this.ranks = Array.isArray(r) ? r : (r?.results || [])
        this.positions = Array.isArray(p) ? p : (p?.results || [])
        this.duties = Array.isArray(d) ? d : (d?.results || [])
      } catch (e) {
        this.error = this.$t('adminDict.errors.loading', { msg: (e?.payload?.detail || e.message) })
      }
    },
    openCreate(kind) {
      this.modal.open = true
      this.modal.kind = kind
      this.modal.editingId = null
      this.modal.form = kind === 'rank' ? emptyRank()
        : kind === 'position' ? emptyPos()
        : emptyDuty()
      this.modal.error = null
    },
    openEdit(kind, row) {
      this.modal.open = true
      this.modal.kind = kind
      this.modal.editingId = row.id
      if (kind === 'rank') {
        this.modal.form = { name: row.name, group: row.group || '' }
      } else if (kind === 'position') {
        this.modal.form = { name: row.name, code: row.code || '' }
      } else {
        this.modal.form = {
          code: row.code,
          name: row.name,
          requires_stpn: !!row.requires_stpn,
          sort_order: row.sort_order ?? 100,
          is_active: row.is_active !== false,
        }
      }
      this.modal.error = null
    },
    closeModal() {
      this.modal.open = false
      this.modal.editingId = null
    },
    async onSubmit() {
      this.modal.error = null
      if (!this.modal.form.name || !this.modal.form.name.trim()) {
        this.modal.error = this.$t('adminDict.errors.nameRequired')
        return
      }
      if (this.modal.kind === 'duty' && !this.modal.form.code?.trim()) {
        this.modal.error = this.$t('adminDict.errors.abbrRequired')
        return
      }
      this.modal.saving = true
      try {
        const payload = { ...this.modal.form }
        if (this.modal.kind === 'position' && !payload.code) delete payload.code
        if (this.modal.kind === 'rank') {
          this.modal.editingId
            ? await updateRank(this.modal.editingId, payload)
            : await createRank(payload)
        } else if (this.modal.kind === 'position') {
          this.modal.editingId
            ? await updatePosition(this.modal.editingId, payload)
            : await createPosition(payload)
        } else {
          this.modal.editingId
            ? await updateDutyType(this.modal.editingId, payload)
            : await createDutyType(payload)
        }
        this.closeModal()
        await this.fetchAll()
      } catch (e) {
        const p = e?.payload
        this.modal.error = (p && (p.detail || Object.values(p).flat().join('; '))) || e.message
      } finally {
        this.modal.saving = false
      }
    },
    async onDelete(kind, row) {
      const label = localizedName(row, this.$i18n.locale) || row.code
      if (!window.confirm(this.$t('adminDict.confirmDelete', { name: label }))) return
      try {
        if (kind === 'rank') await deleteRank(row.id)
        else if (kind === 'position') await deletePosition(row.id)
        else await deleteDutyType(row.id)
        await this.fetchAll()
      } catch (e) {
        this.error = this.$t('adminDict.errors.cantDelete', { msg: (e?.payload?.detail || e.message) })
      }
    },
  },
}
</script>

<style scoped>
.page { background: #fff; border-radius: 16px; padding: 24px; display: flex; flex-direction: column; gap: 20px; }
.page__head { display: flex; align-items: center; gap: 24px; flex-wrap: wrap; }
.page__title { margin: 0; font-size: 24px; font-weight: 700; color: #111827; }
.page__tabs { display: inline-flex; gap: 8px; }
.page__tab { border: 1px solid #E5E7EB; background: #fff; border-radius: 8px; padding: 8px 14px; font-size: 14px; font-weight: 600; color: #374151; cursor: pointer; }
.page__tab.is-active { background: #2F78EE; color: #fff; border-color: #2F78EE; }

.section__head { display: flex; align-items: center; justify-content: space-between; margin-bottom: 12px; }
.section__title { margin: 0; font-size: 18px; color: #111827; font-weight: 700; }

.error-banner { padding: 10px 14px; background: #FEF2F2; border: 1px solid #FCA5A5; color: #991B1B; border-radius: 8px; }

.tbl { width: 100%; border-collapse: collapse; }
.tbl th, .tbl td { padding: 10px 14px; border-bottom: 1px solid #F3F4F6; text-align: center; }
.tbl th { background: #F9FAFB; color: #374151; font-size: 13px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.4px; }
.tbl td { color: #111827; font-size: 14px; }
.tbl td code { font-family: 'Menlo', monospace; background: #F3F4F6; padding: 2px 6px; border-radius: 4px; font-size: 12px; }
.muted { color: #9CA3AF; }
.empty { color: #9CA3AF; padding: 24px; }

.btn { border: 0; cursor: pointer; padding: 8px 14px; border-radius: 8px; font-weight: 600; font-size: 14px; font-family: inherit; }
.btn--primary { background: #2F78EE; color: #fff; }
.btn--primary:hover { background: #1E5BB8; }
.btn--primary:disabled { opacity: 0.6; cursor: not-allowed; }
.btn--secondary { background: #F3F4F6; color: #374151; }
.btn--secondary:hover { background: #E5E7EB; }
.btn--icon { padding: 4px 8px; margin: 0 2px; background: #F3F4F6; }
.btn--icon:hover { background: #E5E7EB; }
.btn--danger { background: #FEE2E2; color: #991B1B; }
.btn--danger:hover { background: #FECACA; }

.modal { position: fixed; inset: 0; background: rgba(0,0,0,0.5); display: flex; align-items: center; justify-content: center; z-index: 1000; }
.modal__panel { background: #fff; width: 480px; max-width: calc(100% - 32px); border-radius: 12px; padding: 24px; }
.modal__title { margin: 0 0 16px; font-size: 20px; font-weight: 700; color: #111827; }
.modal__footer { display: flex; justify-content: flex-end; gap: 8px; margin-top: 16px; }

.form { display: flex; flex-direction: column; gap: 12px; }
.form__row { display: flex; flex-direction: column; gap: 6px; }
.form__label { font-size: 13px; font-weight: 600; color: #374151; }
.req { color: #DC2626; }
.form__input { height: 36px; padding: 6px 12px; border: 1px solid #D1D5DB; border-radius: 8px; font-size: 14px; outline: none; }
.form__input:focus { border-color: #2F78EE; box-shadow: 0 0 0 2px rgba(47,120,238,0.25); }
.form__error { padding: 8px 12px; background: #FEF2F2; border: 1px solid #FCA5A5; color: #991B1B; border-radius: 6px; font-size: 13px; }
.hint { font-size: 12px; color: #6B7280; }
.hint code { background: #F3F4F6; padding: 1px 4px; border-radius: 3px; }

.form__row--check { flex-direction: row; gap: 20px; }
.checkbox { display: inline-flex; align-items: center; gap: 6px; cursor: pointer; font-size: 14px; color: #374151; }
</style>
